import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class CoreInputTagMail implements Plugin {
  private input: HTMLInputElement | null;
  private tagsContainer: HTMLElement | null;
  private keydownHandler: (e: KeyboardEvent) => void;
  private clickHandler: (e: MouseEvent) => void;

  constructor(private readonly element: HTMLElement) {
    this.input = this.element.querySelector(".core-inputTagMail__input");
    this.tagsContainer = this.element.querySelector(".core-inputTagMail__tags");

    this.keydownHandler = (e: KeyboardEvent) => this.handleKeydown(e);
    this.clickHandler = (e: MouseEvent) => this.handleClick(e);
  }

  init(): void {
    if (this.input) {
      this.input.addEventListener("keydown", this.keydownHandler);
    }
    if (this.tagsContainer) {
      this.tagsContainer.addEventListener("click", this.clickHandler);
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      event.preventDefault();
      const value = this.input?.value.trim() || "";
      if (value && this.isValidEmail(value)) {
        this.addTag(value);
        if (this.input) this.input.value = "";
      }
    } else if (event.key === "Backspace" && this.input?.value === "") {
      const tags = this.tagsContainer?.querySelectorAll(".core-tagMail");
      if (tags && tags.length > 0) {
        const lastTag = tags[tags.length - 1] as HTMLElement;
        const email = lastTag.dataset.email || "";
        this.removeTag(lastTag, email);
      }
    }
  }

  private handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const removeBtn = target.closest(".core-tagMail__remove");
    if (removeBtn) {
      const tag = removeBtn.closest(".core-tagMail") as HTMLElement;
      if (tag) {
        const email = tag.dataset.email || "";
        this.removeTag(tag, email);
      }
    }
  }

  private addTag(email: string): void {
    if (!this.tagsContainer) return;

    const existingTags = this.tagsContainer.querySelectorAll(
      `[data-email="${email}"]`,
    );
    if (existingTags.length > 0) return;

    const size = this.element.classList.contains("core-inputTagMail--lg")
      ? "lg"
      : this.element.classList.contains("core-inputTagMail--base")
        ? "base"
        : "sm";

    const tag = document.createElement("div");
    tag.className = `core-tagMail core-tagMail--${size}`;
    tag.dataset.email = email;
    tag.setAttribute("role", "listitem");

    tag.innerHTML = `
      <span class="core-tagMail__label">${email}</span>
      <button
        type="button"
        class="core-tagMail__remove"
        aria-label="Eliminar ${email}"
        data-action="remove"
      >
        <span class="icon__svg--3 core-tagMail__icon dsc-co-icon-clean" aria-hidden="true"></span>
      </button>
    `;

    if (this.input) {
      this.tagsContainer.insertBefore(tag, this.input);
    } else {
      this.tagsContainer.appendChild(tag);
    }

    this.element.dispatchEvent(
      new CustomEvent("addEmail", {
        bubbles: true,
        detail: { email },
      }),
    );

    this.updateState();
  }

  private removeTag(tagElement: HTMLElement, email: string): void {
    tagElement.remove();

    this.element.dispatchEvent(
      new CustomEvent("removeMail", {
        bubbles: true,
        detail: { email },
      }),
    );

    this.updateState();
  }

  private updateState(): void {
    const hasTags =
      this.tagsContainer?.querySelectorAll(".core-tagMail").length || 0;
    this.element.classList.toggle("core-inputTagMail--filled", hasTags > 0);
  }

  private isValidEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  destroy(): void {
    if (this.input) {
      this.input.removeEventListener("keydown", this.keydownHandler);
    }
    if (this.tagsContainer) {
      this.tagsContainer.removeEventListener("click", this.clickHandler);
    }
  }
}

PluginRegistry.register("coreInputTagMail", CoreInputTagMail);
